import React, { memo } from 'react';
import { IconButton, Typography, Tooltip, Box } from '@mui/material';
import { Handle, Position, useReactFlow, useNodeConnections } from '@xyflow/react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Unified node renderer for all workflow views:
// - Builder injects `onEditNode`, which enables the edit/delete buttons and the
//   single-connection handle limits.
// - Instance views pass `variant="instance"`, which switches to status colors
//   (completed = gray, active = lightgreen, upcoming = orange) and drops the
//   tooltip and borders.
// - With neither (Workflow Library view mode), nodes render read-only.

const resolveNodeConfig = (type, variant) => {
    const isPill = type === 'inputNode' || type === 'outputNode';
    const isApproval = type === 'approvalNode' || type.includes('Approval');
    const isDecision = type === 'decisionNode' || type.includes('Decision');
    const shape = isPill ? 'pill' : (isDecision || isApproval) ? 'diamond' : 'task';

    let color;
    if (variant === 'instance') {
        color = isPill ? 'lightgray'
            : type.startsWith('completed') ? 'gray'
                : type.startsWith('active') ? 'lightgreen'
                    : 'orange';
    } else {
        color = isPill ? (type === 'inputNode' ? 'lightgreen' : '#ff4d4d')
            : isApproval ? 'plum'
                : isDecision ? 'pink'
                    : 'lightblue';
    }

    return { shape, isApproval, color };
};

export default memo((nodeDetails) => {
    const { data, id, type, onEditNode, variant } = nodeDetails;
    const { setNodes, setEdges } = useReactFlow();

    const sourceConnections = useNodeConnections({
        handleType: 'source',
    });

    const targetConnections = useNodeConnections({
        handleType: 'target',
    });

    const { shape, isApproval, color } = resolveNodeConfig(type, variant);
    const isInstance = variant === 'instance';
    const interactive = Boolean(onEditNode);
    const border = isInstance ? undefined : `1px solid ${color}`;

    const handleDelete = () => {
        setNodes((nodes) => nodes.filter((node) => node.id !== id));
        setEdges((edges) => edges.filter((edge) => edge.source !== id && edge.target !== id));
    };

    const handleEdit = () => {
        if (onEditNode) onEditNode(id);
    };

    const actionButtons = (
        <>
            <Tooltip title="Edit Node">
                <IconButton
                    size="small"
                    onClick={handleEdit}
                    sx={{
                        padding: '3px',
                        color: 'black',
                        '&:hover': { color: '#1976D2' },
                        '&:focus': { outline: 'none', boxShadow: 'none' },
                    }}
                >
                    <EditIcon sx={{ fontSize: '8px' }} />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete Node">
                <IconButton
                    size="small"
                    onClick={handleDelete}
                    sx={{
                        padding: '3px',
                        color: 'black',
                        '&:hover': { color: '#ff1744' },
                        '&:focus': { outline: 'none', boxShadow: 'none' },
                    }}
                >
                    <DeleteIcon sx={{ fontSize: '8px' }} />
                </IconButton>
            </Tooltip>
        </>
    );

    const getTaskNode = () => {
        return (
            <div style={{
                height: '30px',
                width: '100px',
                border,
                boxShadow: '0 2px 8px lightgray',
                background: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Handle
                    type="target"
                    position={Position.Top}
                    isConnectable={interactive ? (targetConnections.find((edge => edge.target === id)) ? false : true) : true}
                    style={{ background: 'black' }}
                />
                {interactive &&
                    <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex' }}>
                        {actionButtons}
                    </div>
                }
                <Typography sx={{ fontSize: '8px', textAlign: 'center', color: 'black' }}>{data.label}</Typography>
                <Handle
                    type="source"
                    position={Position.Bottom}
                    isConnectable={interactive ? (sourceConnections.find((edge => edge.source === id)) ? false : true) : true}
                    style={{ background: 'black' }}
                />
            </div>
        );
    };

    const getDiamondNode = () => {
        const leftLabel = isApproval ? 'Reject' : 'No';
        const rightLabel = isApproval ? 'Approve' : 'Yes';
        const tooltipRows = isApproval
            ? [
                { label: 'Node Title:', value: data?.nodeTitle || '-' },
                { label: 'Node Description:', value: data?.nodeDescription || '-' },
                { label: 'Approver:', value: data?.approverEmpName || '-' },
            ]
            : [
                { label: 'Node Title:', value: data?.nodeTitle || '-' },
                { label: 'Node Description:', value: data?.nodeDescription || '-' },
                { label: 'Attribute:', value: data?.attributeName || '-' },
                { label: 'Operator:', value: data?.operator || '-' },
                { label: 'Value:', value: data?.conditionValue || '-' },
            ];

        const diamond = (
            <div style={{
                position: 'relative',
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    background: color,
                    border,
                    boxShadow: '0 2px 8px lightgray',
                    transform: 'rotate(45deg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                }}>
                    <Typography sx={{
                        fontSize: '8px',
                        textAlign: 'center',
                        color: 'black',
                        transform: 'rotate(-45deg)',
                        width: '60px',
                        wordBreak: 'break-word'
                    }}>
                        {data.label}
                    </Typography>
                </div>
                <Handle
                    type="target"
                    position={Position.Top}
                    isConnectable={(interactive && isApproval) ? (targetConnections.find((edge => edge.target === id)) ? false : true) : true}
                    style={{
                        background: 'black',
                        position: 'absolute',
                        left: '50%',
                        top: 0,
                        transform: 'translate(-50%, -50%)',
                    }}
                />
                <Typography
                    sx={{
                        position: 'absolute',
                        left: isApproval ? -32 : -25,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '8px',
                        color: 'black',
                        background: color,
                        px: 0.5,
                        borderRadius: '3px'
                    }}
                >
                    {leftLabel}
                </Typography>
                <Handle
                    type="source"
                    id="one"
                    position={Position.Left}
                    isConnectable={interactive ? (sourceConnections.find((edge => edge.source === id && edge.sourceHandle === 'one')) ? false : true) : true}
                    style={{
                        background: 'black',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                />
                <Typography
                    sx={{
                        position: 'absolute',
                        right: isApproval ? -38 : -25,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '8px',
                        color: 'black',
                        background: color,
                        px: 0.5,
                        borderRadius: '3px'
                    }}
                >
                    {rightLabel}
                </Typography>
                <Handle
                    type="source"
                    id="two"
                    position={Position.Right}
                    isConnectable={interactive ? (sourceConnections.find((edge => edge.source === id && edge.sourceHandle === 'two')) ? false : true) : true}
                    style={{
                        background: 'black',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translate(50%, -50%)',
                    }}
                />
                {interactive &&
                    <div style={{ position: 'absolute', top: 2, left: 25 }}>
                        {actionButtons}
                    </div>
                }
            </div>
        );

        if (isInstance) {
            return diamond;
        }

        return (
            <Tooltip title={
                <Box>
                    {tooltipRows.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2" sx={{ textDecoration: 'underline', minWidth: 100 }}>
                                {item.label}
                            </Typography>
                            <Typography variant="subtitle2">{item.value}</Typography>
                        </Box>
                    ))}
                </Box>
            }>
                {diamond}
            </Tooltip>
        );
    };

    const getPillNode = () => {
        return (
            <div style={{
                height: '24px',
                width: '80px',
                border,
                borderRadius: '10px',
                boxShadow: '0 2px 8px lightgray',
                background: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {type === 'outputNode' &&
                    <Handle
                        type="target"
                        position={Position.Top}
                        style={{ background: 'black' }}
                    />
                }
                <Typography sx={{ fontSize: '8px', textAlign: 'center', color: 'black' }}>{data.label}</Typography>
                {type === 'inputNode' &&
                    <Handle
                        type="source"
                        position={Position.Bottom}
                        style={{ background: 'black' }}
                    />
                }
            </div>
        );
    };

    const shapeMap = {
        'pill': getPillNode,
        'diamond': getDiamondNode,
        'task': getTaskNode
    };

    return shapeMap[shape]();
});
